import type { CategoriaDenuncia, CategoriaSugestao, ItemBuyBox, StatusBuyBox } from './types'

/** As 3 seções de preço que sobraram depois da denúncia por vendedor sair pra
 * `outros_vendedores_catalogo` (ver tools/buybox.py `_SECOES_SUGESTAO`) — não recalcula margem
 * aqui, só reshape do que o coletor já decidiu. */
export const SECOES_PRECO_DEF: Array<{ titulo: string; cor: string; categorias: CategoriaSugestao[]; descricao: string }> = [
  {
    titulo: 'Baixar preço',
    cor: '#15803d',
    categorias: ['baixar_preco'],
    descricao:
      'Margem de contribuição no preço necessário pra vencer é maior ou igual a 20% (já descontando ' +
      'comissão, frete, CMV e imposto médio dos últimos 90 dias) e o concorrente é legítimo pra fins de preço.',
  },
  {
    titulo: 'Não baixar preço',
    cor: '#c2410c',
    categorias: ['nao_baixar_preco'],
    descricao: 'Margem no preço necessário pra vencer ficaria abaixo de 20% — baixar comprometeria a rentabilidade.',
  },
  {
    titulo: 'Sem dado para avaliar',
    cor: '#64748b',
    categorias: ['sem_dado'],
    descricao: 'Falta o CMV mais recente e/ou o imposto médio dos últimos 90 dias — sem isso não estimamos.',
  },
]

export type SecaoAgrupada = {
  titulo: string
  cor: string
  descricao: string
  itens: ItemBuyBox[]
}

export function agruparPorSecoes(itens: ItemBuyBox[]): SecaoAgrupada[] {
  return SECOES_PRECO_DEF.map((def) => ({
    titulo: def.titulo,
    cor: def.cor,
    descricao: def.descricao,
    itens: itens.filter((it) => it.sugestao && def.categorias.includes(it.sugestao.categoria)),
  })).filter((secao) => secao.itens.length > 0)
}

/** Uma linha de denúncia — por (item, vendedor), não por item: um mesmo MLB pode gerar mais de
 * uma linha se houver mais de um vendedor irregular no catálogo (ver tools/buybox.py
 * `_agrupar_denuncias`). */
export type LinhaDenuncia = {
  mlb: string
  titulo: string
  marca: string | null
  permalink: string | null
  seller_id: string
  nome_vendedor: string
  categoria: CategoriaDenuncia
  texto: string
}

export type SecaoDenunciaAgrupada = {
  titulo: string
  cor: string
  descricao: string
  linhas: LinhaDenuncia[]
}

export const SECOES_DENUNCIA_DEF: Array<{ titulo: string; cor: string; categoria: CategoriaDenuncia; descricao: string }> = [
  {
    titulo: 'Denunciar — marca exclusiva',
    cor: '#b91c1c',
    categoria: 'denunciar_marca_exclusiva',
    descricao:
      'Marca exclusiva da Fiesta (Mokki Home / Fiesta Party Festa / Fiesta Party Tech) — sem defesa possível, ' +
      'todo vendedor do catálogo deve ser denunciado, ganhando ou não o Buy Box.',
  },
  {
    titulo: 'Denunciar / pedir identificação — grupo Bellocopo',
    cor: '#0e7490',
    categoria: 'denunciar_grupo_pedir_identificacao',
    descricao:
      'Marca do grupo revendável (Bello Festas, Bellocopo, Verocopo, Copobom, Massimo Copo) — pode ser ' +
      'revendida legitimamente. Vendedor ainda não comprovou compra (CNPJ + NF).',
  },
]

export function agruparDenuncias(itens: ItemBuyBox[]): SecaoDenunciaAgrupada[] {
  return SECOES_DENUNCIA_DEF.map((def) => {
    const linhas: LinhaDenuncia[] = []
    for (const it of itens) {
      for (const vendedor of it.outros_vendedores_catalogo ?? []) {
        if (vendedor.denuncia?.categoria === def.categoria) {
          linhas.push({
            mlb: it.mlb,
            titulo: it.titulo,
            marca: it.marca,
            permalink: it.permalink ?? null,
            seller_id: vendedor.seller_id,
            nome_vendedor: vendedor.nome_vendedor,
            categoria: vendedor.denuncia.categoria,
            texto: vendedor.denuncia.texto,
          })
        }
      }
    }
    return { titulo: def.titulo, cor: def.cor, descricao: def.descricao, linhas }
  }).filter((secao) => secao.linhas.length > 0)
}

export const STATUS_LABEL: Record<StatusBuyBox, string> = {
  winning: 'Ganhando',
  competing: 'Perdendo',
  sharing_first_place: 'Empate',
  not_listed: 'Não participa',
  erro: 'Erro',
}

export function formatarMoeda(valor: number | null | undefined): string {
  return valor != null ? `R$ ${valor.toFixed(2).replace('.', ',')}` : '—'
}

export function formatarMargemTexto(item: ItemBuyBox): string {
  if (item.status !== 'competing') return '—'
  const margem = item.margem
  if (!margem) return '—'
  if (margem.dado_faltante) return margem.dado_faltante
  if (margem.margem_pct == null || margem.margem_r == null) return '—'
  return `${margem.margem_pct.toFixed(1)}% (${formatarMoeda(margem.margem_r)})`
}

export function formatarDataHora(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
  } catch {
    return iso
  }
}
